import { AlertTriangle, CheckCircle2, LoaderCircle, X } from "lucide-react";
import type { BulkEdit, BulkEditFailure } from "@/shared/api/api-client";
import { bulkOutcomeWord, bulkProgressVerb } from "./bulk-patch";

/** O lote ainda vai andar? `QUEUED` e `STARTED` — os dois estados ativos
 *  de `BULK_EDIT_ACTIVE_STATUSES`. */
export function isBulkEditRunning(edit: BulkEdit): boolean {
  return edit.status === "QUEUED" || edit.status === "STARTED";
}

/**
 * O andamento e o desfecho de um lote de correção, na própria tela de
 * menções — a pessoa não sai da lista pra acompanhar.
 *
 * Três formas: rodando (contador de itens), concluído (quantas
 * mudaram, quantas já estavam assim, quantas falharam — com a lista de
 * motivos sob demanda) e errado (a mensagem do servidor). Quem decide
 * quando recarregar a lista é a página, ao ver o lote terminar.
 */
export function BulkEditBanner({
  edit,
  failures,
  onShowFailures,
  onDismiss
}: {
  edit: BulkEdit;
  failures: BulkEditFailure[] | null;
  onShowFailures: () => void;
  onDismiss: () => void;
}) {
  const { counters } = edit;
  const done = counters.total - counters.pending;

  if (isBulkEditRunning(edit)) {
    return (
      <section className="card bulk-banner" aria-live="polite">
        <p className="bulk-banner-head">
          <LoaderCircle className="spin" size={16} aria-hidden="true" />
          {bulkProgressVerb(edit.patch)} {counters.total.toLocaleString("pt-BR")} menções em lote…{" "}
          {done.toLocaleString("pt-BR")} de {counters.total.toLocaleString("pt-BR")}
        </p>
        <progress
          className="bulk-banner-progress"
          max={counters.total}
          value={done}
          aria-label="Andamento do lote"
        />
      </section>
    );
  }

  if (edit.status === "ERRORED") {
    return (
      <section className="card bulk-banner is-error" aria-live="polite">
        <p className="bulk-banner-head">
          <AlertTriangle size={16} aria-hidden="true" />
          O lote parou com erro
          {edit.message ? `: ${edit.message}` : "."}{" "}
          {done > 0 &&
            `${done.toLocaleString("pt-BR")} de ${counters.total.toLocaleString("pt-BR")} menções foram processadas antes.`}
        </p>
        <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
          <X size={16} />
        </button>
      </section>
    );
  }

  return (
    <section
      className={`card bulk-banner ${counters.failed ? "has-failures" : "is-done"}`}
      aria-live="polite"
    >
      <p className="bulk-banner-head">
        <CheckCircle2 size={16} aria-hidden="true" />
        Lote concluído: {counters.changed.toLocaleString("pt-BR")}{" "}
        {counters.changed === 1 ? "menção" : "menções"} {bulkOutcomeWord(edit.patch, counters.changed)}
        {counters.unchanged > 0 &&
          ` · ${counters.unchanged.toLocaleString("pt-BR")} já ${counters.unchanged === 1 ? "estava" : "estavam"} assim`}
        {counters.failed > 0 &&
          ` · ${counters.failed.toLocaleString("pt-BR")} com erro`}
      </p>
      <div className="bulk-banner-actions">
        {counters.failed > 0 && failures === null && (
          <button type="button" className="text-button" onClick={onShowFailures}>
            Ver erros
          </button>
        )}
        <button type="button" className="icon-button" onClick={onDismiss} aria-label="Fechar aviso">
          <X size={16} />
        </button>
      </div>
      {failures && failures.length > 0 && (
        <ul className="bulk-failures">
          {failures.map(failure => (
            <li key={failure.mention_id}>
              Menção #{failure.mention_id}: {failure.message ?? "erro não informado"}
            </li>
          ))}
          {counters.failed > failures.length && (
            <li>… e mais {counters.failed - failures.length}.</li>
          )}
        </ul>
      )}
    </section>
  );
}
